import { type MouseEvent, useEffect, useState } from 'react'

import { useTranslation } from 'react-i18next'
import { useQuery } from 'react-query'
import { useNavigate } from 'react-router-dom'
import { Swiper, SwiperSlide } from 'swiper/react'

import {
  IconMediaHeart,
  IconMediaSubscriptions,
  IconPurchases
} from 'assets/svg/sprite'
import { addToast } from 'components/Toast/Toast'
import { usePageTitle } from 'context/pageTitleContext'
import DesktopAdditionalContent from 'features/DesktopAdditionalContent/DesktopAdditionalContent'
import { computeUnlockStatus } from 'helpers/media'
import MediaHomeModal from 'pages/mediaHomeModal'
import { getMediaData } from 'services/endpoints/media'
import { useUserProfile } from 'store'
import { useMediaCacheStore } from 'store/mediaCacheStore'
import {
  type IPlaylist,
  type IPost,
  type ISubscriptionHistoryItem
} from 'types/interfaces/ITypes'

import { MediaItem } from './components/MediaItem'
import { MediaItemOptions } from './components/MediaItemOptions'
import { MediaItemPlaylist } from './components/MediaItemPlaylist'
import { MediaItemSmall } from './components/MediaItemSmall'
import { MediaSection } from './components/MediaSection'
import { SeeMoreItem } from './components/SeeMoreItem'
import styles from './media.module.scss'

import 'swiper/css'

type InlinePlayback = {
  postId: string
  videoId: string
  time: number
  duration: number
  playing: boolean
}

const normalizePurchase = (
  item: IPost,
  entityType: 'message' | 'post'
) => {
  const unlocked = computeUnlockStatus(item)

  return {
    ...item,
    type: entityType,
    is_unlocked: unlocked,
    price: unlocked ? null : item.price
  }
}

export const Media = () => {
  const { setTitle } = usePageTitle()
  const { t } = useTranslation()
  const navigate = useNavigate()
  const userData = useUserProfile()

  const { getMedia, setMedia } = useMediaCacheStore()

  const [isOptionsNavOpen, setIsOptionsNavOpen] = useState(false)
  const [optionsType, setOptionsType] = useState('')
  const [activePostId, setActivePostId] = useState<string | null>(null)
  const [activeItemType, setActiveItemType] = useState<'post' | 'message'>('post')

  const [inlinePlayback, setInlinePlayback] =
    useState<InlinePlayback | null>(null)

  const [previewOpen, setPreviewOpen] = useState(false)
  const [previewFeed, setPreviewFeed] = useState<IPost[] | null>(null)
  const [previewIndex, setPreviewIndex] = useState(0)

  const blockedUserIds =
    userData?.blocke_by_model?.map((user: any) => user.id) ?? []

  const cached = getMedia('self')

  const normalizedCached = cached
    ? {
        subscriptions: Array.isArray(cached.subscriptions)
          ? cached.subscriptions
          : [],
        playlists: Array.isArray(cached.playlists)
          ? cached.playlists
          : [],
        like: Array.isArray(cached.like)
          ? cached.like
          : [],
        watched: Array.isArray(cached.watched)
          ? cached.watched
          : [],
        purchases: {
          messages: Array.isArray(cached.purchases?.messages)
            ? cached.purchases.messages
            : [],
          posts: Array.isArray(cached.purchases?.posts)
            ? cached.purchases.posts
            : []
        }
      }
    : undefined

  const { data, isLoading } = useQuery(
    ['getMediaData'],
    getMediaData,
    {
      initialData: normalizedCached,
      onSuccess: fresh => setMedia('self', fresh),
      refetchOnMount: true
    }
  )

  useEffect(() => {
    setTitle(t('media'))
  }, [setTitle, t])

  const subscriptions = data?.subscriptions ?? []
  const playlists = data?.playlists ?? []
  const likes = data?.like ?? []

  const purchases: IPost[] = [
    ...(data?.purchases?.messages ?? []).map((item: IPost) =>
      normalizePurchase(item, 'message')
    ),
    ...(data?.purchases?.posts ?? []).map((item: IPost) =>
      normalizePurchase(item, 'post')
    )
  ]

  const openPreview = (
    list: IPost[],
    item: IPost,
    index: number,
    event: MouseEvent
  ) => {
    const element = event.target as HTMLElement
    const selection = window.getSelection?.()

    const clickedControl =
      element.closest('.media__options') ??
      element.closest('[data-prevent-open]') ??
      element.closest('button') ??
      element.closest('a')

    if (clickedControl ?? selection?.toString()) {
      return
    }

    const playback =
      inlinePlayback?.postId === String(item.id)
        ? inlinePlayback
        : null

    if (playback?.playing) {
      setInlinePlayback({
        ...playback,
        playing: false
      })
    }

    const previewList = list.map((post, postIndex) => {
      if (postIndex !== index) {
        return post
      }

      return {
        ...post,
        video_resume_time: playback?.time ?? 0,
        video_resume_duration:
          playback?.duration ?? post.video_resume_duration,
        video_resume_playing: true
      }
    })

    setPreviewFeed(previewList)
    setPreviewIndex(index)
    setPreviewOpen(true)
  }

  const renderSubscriptionSkeletons = (key: string) => (
    <Swiper spaceBetween={10} slidesPerView='auto'>
      {Array.from({ length: 3 }).map((_, index) => (
        <SwiperSlide
          key={`${key}-loader-${index}`}
          style={{ width: 'auto' }}
        >
          <div className={styles.mediaItemSmallSkeleton}>
            <div className={styles.skeletonCover}>
              <div className={styles.skeletonAvatar} />
            </div>

            <div className={styles.skeletonTitle} />
            <div className={styles.skeletonSubtitle} />
          </div>
        </SwiperSlide>
      ))}
    </Swiper>
  )

  const renderPostSkeletons = (key: string) => (
    <Swiper spaceBetween={10} slidesPerView='auto'>
      {Array.from({ length: 2 }).map((_, index) => (
        <SwiperSlide
          key={`${key}-loader-${index}`}
          style={{ width: 'auto' }}
        >
          <div className={styles.mediaPostSkeleton}>
            <div className={styles.skeletonPostImage} />

            <div className={styles.skeletonPostMeta}>
              <div className={styles.skeletonPostAvatar} />
              <div className={styles.skeletonPostText} />
              <div className={styles.skeletonPostDate} />
            </div>
          </div>
        </SwiperSlide>
      ))}
    </Swiper>
  )

  const mediaSections = [
    {
      key: 'subscriptions',
      icon: <IconMediaSubscriptions />,
      title: 'subscriptions',
      action: '/subscriptions',
      data: subscriptions,
      renderItem: (item: ISubscriptionHistoryItem) => (
        <SwiperSlide
          key={item.userId}
          style={{ width: 'auto' }}
        >
          <MediaItemSmall
            active={false}
            data={item}
            blockedUserId={blockedUserIds}
            userData={userData}
            clickFn={() => {
              if (
                blockedUserIds.includes(item.userId) &&
                userData?.role === 'fan'
              ) {
                addToast('error', 'user is blocked you.')
                return
              }

              navigate(`/${item.userName}`)
            }}
          />
        </SwiperSlide>
      ),
      emptyText: 'You have no active subscriptions'
    },
    {
      key: 'purchases',
      icon: <IconPurchases />,
      title: 'purchases',
      action: '/purchases',
      data: purchases,
      renderItem: (post: IPost, index: number) => (
        <SwiperSlide
          key={post.id}
          style={{ width: 'auto' }}
        >
          <div
            onClick={event =>
              openPreview(purchases, post, index, event)
            }
          >
            <MediaItem
              postData={post}
              isOpen={isOptionsNavOpen}
              setIsOpen={open => {
                setOptionsType(open ? 'purchase' : '')
                setIsOptionsNavOpen(open)
              }}
              setIsActive={id => {
                setActivePostId(id)
                setActiveItemType(post.type === 'message' ? 'message' : 'post')
              }}
              hideLikeIfDeleted
              onVideoPlaybackChange={state => {
                setInlinePlayback({
                  postId: String(post.id),
                  ...state
                })
              }}
            />
          </div>
        </SwiperSlide>
      ),
      emptyText: 'You have not made any purchases'
    },
    ...(userData?.show_liked_history
      ? [
          {
            key: 'likes',
            icon: <IconMediaHeart />,
            title: 'Likes',
            action: '/likes',
            data: likes,
            renderItem: (like: IPost, index: number) => (
              <SwiperSlide
                key={like.id}
                style={{ width: 'auto' }}
              >
                <div
                  onClick={event =>
                    openPreview(
                      likes as IPost[],
                      like,
                      index,
                      event
                    )
                  }
                >
                  <MediaItem
                    postData={like}
                    isOpen={isOptionsNavOpen}
                    setIsOpen={open => {
                      setOptionsType(open ? 'likes' : '')
                      setIsOptionsNavOpen(open)
                    }}
                    setIsActive={setActivePostId}
                    onVideoPlaybackChange={state => {
                      setInlinePlayback({
                        postId: String(like.id),
                        ...state
                      })
                    }}
                  />
                </div>
              </SwiperSlide>
            ),
            emptyText: 'You have not liked anything so far'
          }
        ]
      : []),
    {
      key: 'playlists',
      icon: <IconPurchases />,
      title: 'custom playlists',
      action: '/playlists',
      data: playlists,
      renderItem: (playlist: IPlaylist) => (
        <SwiperSlide
          key={playlist.playlist_info.id}
          style={{ width: 'auto' }}
        >
          <MediaItemPlaylist
            data={data}
            playlistData={playlist}
            active={activePostId === playlist.playlist_info.id}
            setIsActive={() => {
              setActivePostId(playlist.playlist_info.id)
              navigate(`/playlists/${playlist.playlist_info.id}`)
            }}
          />
        </SwiperSlide>
      ),
      emptyText: 'You have no playlists created'
    }
  ] as Array<{
    key: string
    icon: JSX.Element
    title: string
    action: string
    data: any[]
    renderItem: (...args: any[]) => JSX.Element
    emptyText: string
  }>

  const closeOptions = () => {
    setIsOptionsNavOpen(false)
    setActivePostId(null)
    setActiveItemType('post')
    setOptionsType('')
  }

  return (
    <div className='media-layout media__content__wrapper'>
      <div className={styles.media}>
        {mediaSections.map(
          ({
            key,
            icon,
            title,
            action,
            data,
            renderItem,
            emptyText
          }) => {
            const showLoading = isLoading && !data.length

            return (
              <MediaSection
                key={key}
                icon={icon}
                title={title}
                action={action}
                hasData={!!data.length || showLoading}
              >
                {showLoading ? (
                  key === 'subscriptions'
                    ? renderSubscriptionSkeletons(key)
                    : renderPostSkeletons(key)
                ) : data.length ? (
                  <Swiper
                    spaceBetween={10}
                    slidesPerView='auto'
                  >
                    {data.map(renderItem)}

                    <SwiperSlide
                      style={{
                        width: '11rem',
                        height: '16rem'
                      }}
                    >
                      <SeeMoreItem
                        sectionKey={key}
                        text='See More'
                        action={action}
                      />
                    </SwiperSlide>
                  </Swiper>
                ) : (
                  <div className={styles.empty}>
                    <p>{emptyText}</p>
                  </div>
                )}
              </MediaSection>
            )
          }
        )}
      </div>

      {activePostId && isOptionsNavOpen && (
        <>
          <div
            className={styles.optionsBackdrop}
            onClick={closeOptions}
          />

          <MediaItemOptions
            role='model'
            type={optionsType}
            isOpen={isOptionsNavOpen}
            setIsOpen={open => {
              if (open) {
                setIsOptionsNavOpen(true)
              } else {
                closeOptions()
              }
            }}
            itemId={activePostId}
            itemType={activeItemType}
          />
        </>
      )}

      {previewOpen && previewFeed && (
        <MediaHomeModal
          feed={previewFeed}
          initialIndex={previewIndex}
          closeFn={() => setPreviewOpen(false)}
        />
      )}

      <DesktopAdditionalContent />
    </div>
  )
}